import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Message handling for monitoring
  messages: [], // WebSocket messages for monitoring processing only
  totalMessagesReceived: 0,
  recentMessageCount: 0,
  lastMessageReceivedAt: null,
  latencyStats: { p50: null, p95: null, p99: null },
  latencySamples: [], // bounded rolling buffer of leader_latency_ms values
  messageRate: null, // messages per second, rolling 60s window
  // Data monitoring-specific state
  timeSeriesData: [],
  selectedInterval: null,
  selectedIntervalMessages: [],
  lastUpdateTime: null,
  // Data Monitor UI state persistence
  persistentSourceColors: {},
  persistentSourceOrder: [],
  sourceLastSeen: {},
  channelLastSeen: {},
  selectedSource: 'total',
  // Video monitoring-specific state
  videoThumbnails: {}, // sourceId -> Array of { thumbnailS3Key, timestamp, recordId, sourceName } (up to 5)
  videoSourceActivity: {}, // sourceId -> { lastSeen, messageCount, isActive, sourceName }
  lastVideoUpdateTime: null,
  // ATR monitoring-specific state
  atrThumbnails: {}, // sourceId -> Array of { thumbnailS3Key, timestamp, recordId, sourceName } (up to 30)
  atrSourceActivity: {}, // sourceId -> { lastSeen, messageCount, isActive, sourceName }
  lastATRUpdateTime: null,
  // Filmstrip state persistence
  filmstripState: {}, // sourceId -> { filmstripData, presignedUrls }
};

const monitoringSlice = createSlice({
  name: 'monitoring',
  initialState,
  reducers: {
    // Message handling actions
    clearMonitoringMessages: (state) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn('🚨 clearMonitoringMessages called - clearing', state.messages.length, 'messages');
        console.trace('Stack trace for clearMonitoringMessages');
      }
      state.messages = [];
      state.totalMessagesReceived = 0;
      state.recentMessageCount = 0;
      state.lastMessageReceivedAt = null;
    },
    updateLatencyStats: (state, action) => {
      const incoming = (action.payload || []).filter(v => typeof v === 'number' && isFinite(v));
      if (incoming.length === 0) return;

      const MAX_SAMPLES = 10000;
      const combined = state.latencySamples.concat(incoming);
      state.latencySamples = combined.length > MAX_SAMPLES ? combined.slice(-MAX_SAMPLES) : combined;

      const sorted = state.latencySamples.slice().sort((a, b) => a - b);
      const pct = (p) => sorted[Math.max(0, Math.ceil((p / 100) * sorted.length) - 1)];
      state.latencyStats = { p50: pct(50), p95: pct(95), p99: pct(99) };
    },
    recordMonitoringMessageBatch: (state, action) => {
      const {
        acceptedCount = 0,
        totalMessagesReceived,
        recentMessageCount,
        lastMessageReceivedAt
      } = action.payload || {};

      if (acceptedCount <= 0 && totalMessagesReceived === undefined) {
        return;
      }

      if (typeof totalMessagesReceived === 'number') {
        state.totalMessagesReceived = totalMessagesReceived;
      } else {
        state.totalMessagesReceived += acceptedCount;
      }

      if (typeof recentMessageCount === 'number') {
        state.recentMessageCount = recentMessageCount;
      }

      if (lastMessageReceivedAt) {
        state.lastMessageReceivedAt = lastMessageReceivedAt;
      }
    },
    // Data monitoring actions
    setSelectedInterval: (state, action) => {
      state.selectedInterval = action.payload;
    },
    setSelectedIntervalMessages: (state, action) => {
      state.selectedIntervalMessages = action.payload;
    },
    setLastUpdateTime: (state, action) => {
      state.lastUpdateTime = action.payload;
    },
    applyDataMonitoringSnapshot: (state, action) => {
      const {
        timeSeriesData,
        lastUpdateTime,
        messageRate,
        sourceLastSeen,
        channelLastSeen,
        persistentSourceOrder
      } = action.payload || {};

      if (timeSeriesData !== undefined) {
        state.timeSeriesData = timeSeriesData;
      }
      if (lastUpdateTime !== undefined) {
        state.lastUpdateTime = lastUpdateTime;
      }
      if (messageRate !== undefined) {
        state.messageRate = messageRate;
      }
      if (sourceLastSeen !== undefined) {
        state.sourceLastSeen = sourceLastSeen;
      }
      if (channelLastSeen !== undefined) {
        state.channelLastSeen = channelLastSeen;
      }
      if (persistentSourceOrder !== undefined) {
        state.persistentSourceOrder = persistentSourceOrder;
      }
    },
    clearMonitoringData: (state) => {
      state.messages = [];
      state.totalMessagesReceived = 0;
      state.recentMessageCount = 0;
      state.lastMessageReceivedAt = null;
      state.latencyStats = { p50: null, p95: null, p99: null };
      state.latencySamples = [];
      state.messageRate = null;
      state.timeSeriesData = [];
      state.selectedInterval = null;
      state.selectedIntervalMessages = [];
      state.lastUpdateTime = null;
      state.persistentSourceColors = {};
      state.persistentSourceOrder = [];
      state.sourceLastSeen = {};
      state.channelLastSeen = {};
      state.selectedSource = 'total';
    },
    // Data Monitor UI state persistence actions
    setPersistentSourceColors: (state, action) => {
      state.persistentSourceColors = action.payload;
    },
    setPersistentSourceOrder: (state, action) => {
      state.persistentSourceOrder = action.payload;
    },
    setSourceLastSeen: (state, action) => {
      state.sourceLastSeen = action.payload;
    },
    setSelectedSource: (state, action) => {
      state.selectedSource = action.payload;
    },
    // Video monitoring actions
    setVideoThumbnails: (state, action) => {
      state.videoThumbnails = action.payload;
    },
    setVideoSourceActivity: (state, action) => {
      state.videoSourceActivity = action.payload;
    },
    setLastVideoUpdateTime: (state, action) => {
      state.lastVideoUpdateTime = action.payload;
    },
    applyVideoMonitoringSnapshot: (state, action) => {
      const {
        videoThumbnails,
        videoSourceActivity,
        lastVideoUpdateTime
      } = action.payload || {};

      if (videoThumbnails !== undefined) {
        state.videoThumbnails = videoThumbnails;
      }
      if (videoSourceActivity !== undefined) {
        state.videoSourceActivity = videoSourceActivity;
      }
      if (lastVideoUpdateTime !== undefined) {
        state.lastVideoUpdateTime = lastVideoUpdateTime;
      }
    },
    clearVideoMonitoringData: (state) => {
      state.videoThumbnails = {};
      state.videoSourceActivity = {};
      state.lastVideoUpdateTime = null;
      state.filmstripState = {};
    },
    // ATR monitoring actions
    setATRThumbnails: (state, action) => {
      state.atrThumbnails = action.payload;
    },
    setATRSourceActivity: (state, action) => {
      state.atrSourceActivity = action.payload;
    },
    setLastATRUpdateTime: (state, action) => {
      state.lastATRUpdateTime = action.payload;
    },
    applyATRMonitoringSnapshot: (state, action) => {
      const {
        atrThumbnails,
        atrSourceActivity,
        lastATRUpdateTime
      } = action.payload || {};

      if (atrThumbnails !== undefined) {
        state.atrThumbnails = atrThumbnails;
      }
      if (atrSourceActivity !== undefined) {
        state.atrSourceActivity = atrSourceActivity;
      }
      if (lastATRUpdateTime !== undefined) {
        state.lastATRUpdateTime = lastATRUpdateTime;
      }
    },
    clearATRMonitoringData: (state) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn('🚨 clearATRMonitoringData called - clearing ATR state');
        console.trace('Stack trace for clearATRMonitoringData');
      }
      state.atrThumbnails = {};
      state.atrSourceActivity = {};
      state.lastATRUpdateTime = null;
    },
    // Filmstrip state management
    setFilmstripState: (state, action) => {
      const { sourceId, filmstripData, presignedUrls, allThumbnails, displayedThumbnails } = action.payload;
      state.filmstripState[sourceId] = {
        // Support both old and new filmstrip formats
        filmstripData: filmstripData || displayedThumbnails, // For backward compatibility
        allThumbnails, // For ATR filmstrip
        displayedThumbnails, // For ATR filmstrip
        presignedUrls,
        lastUpdated: Date.now()
      };
    },
    clearFilmstripState: (state, action) => {
      const { sourceId } = action.payload;
      if (sourceId) {
        delete state.filmstripState[sourceId];
      } else {
        state.filmstripState = {};
      }
    },
    // Clear all monitoring data
    clearAllMonitoringData: (state) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('🚪 clearAllMonitoringData called - clearing ALL monitoring state including filmstrip');
        console.log('📊 State being cleared:', {
          messages: state.messages.length,
          videoThumbnails: Object.keys(state.videoThumbnails).length,
          atrThumbnails: Object.keys(state.atrThumbnails).length,
          filmstripState: Object.keys(state.filmstripState).length
        });
      }
      // Reset to initial state - explicitly set each property to avoid reference issues
      state.messages = [];
      state.latencyStats = { p50: null, p95: null, p99: null };
      state.latencySamples = [];
      state.messageRate = null;
      state.timeSeriesData = [];
      state.selectedInterval = null;
      state.selectedIntervalMessages = [];
      state.lastUpdateTime = null;
      state.persistentSourceColors = {};
      state.persistentSourceOrder = [];
      state.sourceLastSeen = {};
      state.channelLastSeen = {};
      state.selectedSource = 'total';
      state.videoThumbnails = {};
      state.videoSourceActivity = {};
      state.lastVideoUpdateTime = null;
      state.atrThumbnails = {};
      state.atrSourceActivity = {};
      state.lastATRUpdateTime = null;
      state.filmstripState = {}; // This clears the filmstrip state
    },
  },
});

export const {
  clearMonitoringMessages,
  updateLatencyStats,
  recordMonitoringMessageBatch,
  setSelectedInterval,
  setSelectedIntervalMessages,
  setLastUpdateTime,
  applyDataMonitoringSnapshot,
  clearMonitoringData,
  setPersistentSourceColors,
  setPersistentSourceOrder,
  setSourceLastSeen,
  setSelectedSource,
  setVideoThumbnails,
  setVideoSourceActivity,
  setLastVideoUpdateTime,
  applyVideoMonitoringSnapshot,
  clearVideoMonitoringData,
  setATRThumbnails,
  setATRSourceActivity,
  setLastATRUpdateTime,
  applyATRMonitoringSnapshot,
  clearATRMonitoringData,
  setFilmstripState,
  clearFilmstripState,
  clearAllMonitoringData,
} = monitoringSlice.actions;

export default monitoringSlice.reducer;
